import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../../constants/routes';
import { UI, DISPLAY, EASE, h2, lead, sh, pill, SECTION_GAP } from '../../styles/tl5';
import imgLipid from '../../assets/img/v5/guide-lipid.jpg';
import imgHba1c from '../../assets/img/v5/guide-hba1c.jpg';
import imgThyroid from '../../assets/img/v5/guide-thyroid.jpg';
import imgPrep from '../../assets/img/v5/guide-prep.jpg';

// "Understand your results" — four photo cards.
//
// The article records carry an icon and a gradient, not a photograph, so
// the four reference photographs are cycled as the banner. Titles,
// categories and summaries are the real published articles; the static
// set below is only used when nothing is published yet.
const PHOTOS = [imgLipid, imgHba1c, imgThyroid, imgPrep];

const FALLBACK = [
  { category: 'Heart', title: 'What your lipid profile is telling you', excerpt: 'HDL, LDL and triglycerides in plain words.' },
  { category: 'Diabetes', title: 'HbA1c vs fasting sugar, explained', excerpt: 'Why your doctor may ask for both.' },
  { category: 'Thyroid', title: 'Reading T3, T4 and TSH', excerpt: 'Common patterns and what they can mean.' },
  { category: 'Preparation', title: 'How to prepare for a blood test', excerpt: 'Fasting, water, medicines and timing.' },
];

const Guides = ({ articles = [] }) => {
  const items = (articles.length ? articles.slice(0, 4) : FALLBACK).map((a, i) => ({
    key: a.id || a.slug || a.title,
    category: a.category || 'Health',
    title: a.title,
    excerpt: a.excerpt,
    photo: PHOTOS[i % PHOTOS.length],
    href: a.slug ? `${ROUTES.BLOG}/${a.slug}` : ROUTES.BLOG,
  }));

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1320,
        mx: 'auto',
        pt: SECTION_GAP,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <Box sx={{ ...sh, mb: 0, px: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography component="h2" sx={h2}>Understand your results</Typography>
          <Typography sx={{ ...lead, fontSize: 15 }}>
            Plain-language guides, reviewed by TrustLab doctors.
          </Typography>
        </Box>
        <Box component={Link} to={ROUTES.BLOG} sx={pill}>All guides →</Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
          gap: 1.5,
        }}
      >
        {items.map((g) => (
          <Box
            key={g.key}
            component={Link}
            to={g.href}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '24px',
              overflow: 'hidden',
              bgcolor: '#FFFFFF',
              color: 'var(--tl-g9)',
              textDecoration: 'none',
              transition: `transform .5s ${EASE}, box-shadow .5s ${EASE}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)',
                color: 'var(--tl-g9)',
              },
              '&:hover img': { transform: 'scale(1.06)' },
            }}
          >
            <Box
              component="img"
              src={g.photo}
              alt=""
              sx={{
                width: '100%',
                aspectRatio: '16 / 10',
                objectFit: 'cover',
                display: 'block',
                bgcolor: 'var(--tl-line)',
                transition: `transform 1.2s ${EASE}`,
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', p: '16px 18px 20px' }}>
              <Box
                component="span"
                sx={{
                  fontFamily: UI,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--tl-teal-d)',
                }}
              >
                {g.category}
              </Box>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>
                {g.title}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                {g.excerpt}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Guides;
